import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Type } from 'class-transformer';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ABaseTreeNode } from '../../../components/common/basetreecontrol/base.tree.control';
import { StringHelper } from '../../../helpers/array.helpers';
import { ClassTransformerHelper } from '../../../helpers/class.transformer.helper';
import { EnumHelper } from '../../../helpers/enum.helper';
import { InjectorHelper } from '../../../helpers/injector.helper';
import { BodyType } from '../../../models/enums/bodytype.enum';
import { MethodType } from '../../../models/enums/methodtype.enum';
import { DataModelService, DataSourceQuery } from '../../../services/datamodel.service';
import { WorkflowModuleExecuter, WorkflowStatus } from '../../../models/workflow/workflow.model';
import { InterceptorSkipHeader } from '../../../services/interceptor.skip.header';
import { SettingsService } from '../../../services/settings.service';
import { ObsoleteInfo, WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../services/workflow.service';
import { DataCheck, FormulaWorkflowDialogContent } from '../../workflow.dialog';
import { WorkflowFormulaCalculator } from '../definevalue/define.value.settings';

// @dynamic
export abstract class ARestCallSettings extends FormulaWorkflowDialogContent {

    Data: ARestCallSettingsData;
    RestTypes;
    BodyTypes;
    BodyContentTypes = ['application/json', 'application/xml'];
    /** REST data sources for the data source option (service workflows). */
    RestQuellen: { SID: string; Caption: string }[] = [];

    constructor(protected translate: TranslateService) {
        super();
        this.RestTypes = EnumHelper.GetDropdownValues(MethodType);
        this.BodyTypes = EnumHelper.GetDropdownValues(BodyType);
        // REST data sources and Haufe X360 (OAuth2) -- the server serves both (IRestDatenquelle).
        const dienst = InjectorHelper.InjectorInstance.get(DataModelService);
        [REST_DATENQUELLE, HAUFE_DATENQUELLE].forEach(typ => {
            const abfrage = new DataSourceQuery('00000000-0000-0000-0000-000000000000');
            abfrage.DBInfoType = typ;
            dienst.GetDataSourcesByQuery(abfrage).subscribe(liste => {
                this.RestQuellen = [...this.RestQuellen, ...(liste ?? [])].sort((x, y) => (x.Caption ?? '').localeCompare(y.Caption ?? ''));
            });
        });
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (this.Data) {
            const errorList = [];
            this.Data.Params.forEach(param => {
                if (!param.Value || param.Value === '') {
                    errorList.push(StringHelper.format(this.translate.instant('@@Fehlender Wert fuer Parameter {0}'), [param.Key]));
                }
                if (!param.Key || param.Key === '') {
                    errorList.push(StringHelper.format(this.translate.instant('@@Fehlender Schluessel fuer Parameterwert {0}'),
                        [param.Value]));
                }
            });
            this.Data.Headers.forEach(param => {
                if (!param.Value || param.Value === '') {
                    errorList.push(StringHelper.format(this.translate.instant('@@Fehlender Wert fuer Header {0}'), [param.Key]));
                }
                if (!param.Key || param.Key === '') {
                    errorList.push(StringHelper.format(this.translate.instant('@@Fehlender Schluessel fuer Headerwert {0}'),
                        [param.Value]));
                }
            });
            this.checkForErrors(errorList);
            if (errorList.length > 0) {
                errorList.splice(0, 0,
                    this.translate.instant('@@Fehlerhafter Rest-Aufruf. Bitte korrigieren Sie folgende Einstellungen:\n'));
                retVal.IsCorrect = false;
                retVal.Error = errorList.join('\n');
            }
        }
        return retVal;
    }

    checkForErrors(errorList: string[]) {
    }

    getResult(): any {
        return this.Data;
    }

    addParam() {
        this.Data.Params.push(new RestParamData());
    }

    removeParam(param) {
        const index = this.Data.Params.indexOf(param);
        if (index >= 0) {
            this.Data.Params.splice(index, 1);
        }
    }

    addHeader() {
        this.Data.Headers.push(new RestParamData());
    }

    removeHeader(header) {
        const index = this.Data.Headers.indexOf(header);
        if (index >= 0) {
            this.Data.Headers.splice(index, 1);
        }
    }

    onVariableSelect(event, param) {
        if (event && param) {
            param.Value = event.Caption;
        }
    }

    onBodyVariableSelect(event) {
        if (event) {
            this.Data.BodyContent = event.Caption;
        }
    }

    onPathSelect(event) {
        if (event) {
            this.Data.Path = event.Caption;
        }
    }

    onServiceURLChange() {
        if (this.Data.UseServiceURL) {
            if (!this.BodyContentTypes.some(x => x === this.Data.BodyContentType)) {
                this.Data.BodyContentType = 'application/json';
            }
        }
    }

    abstract getPreview(): string;
}

@Component({
    selector: 'restCall-settings',
    templateUrl: './restCall.settings.html',
    styleUrls: ['./restCall.settings.css']
})
export class RestCallSettings extends ARestCallSettings {
    VariableNodes: ABaseTreeNode[] = [];

    static ModuleID = 'restWFModule';

    public static getURL(status: WorkflowStatus) {
        let retVal = '';
        if (status) {
            const data = status.ActualSettings;
            if (data) {
                if (data.UseServiceURL) {
                    retVal = SettingsService.API_BASE_URL.getValue();
                } else {
                    if (data.Address) {
                        retVal += data.IsSSL ? 'https://' : 'http://';
                        retVal += data.Address + ':' + data.Port + '/';
                    } else {
                        retVal += SettingsService.API_BASE_URL.getValue();
                    }
                }
                if (data.Path) {
                    if (data.GetPathFromStatus === true) {
                        retVal += status.Context.get(data.Path);
                    } else {
                        retVal += data.Path;
                    }
                }
                if (data.Params && data.Params.length > 0) {
                    retVal += '?';
                    let first = true;
                    data.Params.forEach(v => {
                        if (!first) {
                            retVal += '&';
                        }
                        first = false;
                        let val = '';
                        if (v.ReadFromStatus) {
                            const stateVal = status.Context.get(v.Value);
                            if (stateVal) {
                                val = stateVal.toString();
                            }
                        } else {
                            val = v.Value;
                        }
                        retVal += data.UrlEncodeParams ? encodeURIComponent(v.Key) + '=' + encodeURIComponent(val ?? '') : v.Key + '=' + val;
                    });
                }
            }
        }
        return retVal;
    }

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = RestCallSettings.ModuleID;
        reg.Caption = '@@Restaufruf';
        reg.GroupID = 'wfActions';
        reg.Index = 30;
        reg.SettingsControl = RestCallSettings;
        reg.SettingsTypeHelper = new RestCallSettingsDataHelper();
        reg.Executer = RestCallModuleExecuter;
        reg.ObsoleteInfo = new ObsoleteInfo();
        reg.ObsoleteInfo.ReplacedBy = RestCallAdvancedSettings.ModuleID;
        return reg;
    }

    constructor(protected translate: TranslateService) {
        super(translate);
    }

    initialize(data: any) {
        super.initialize(data);
        let index = 0;
        const varNodes = [];
        this.Variables.forEach(x => {
            const btn = new ABaseTreeNode(index++);
            btn.Caption = x.Name;
            varNodes.push(btn);
        });
        this.VariableNodes = varNodes;
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, RestCallSettingsData);
        } else {
            this.Data = newSettings(new RestCallSettingsData());
        }
    }

    getPreview(): string {
        const state = new WorkflowStatus('', '');
        state.ActualSettings = this.Data;
        return RestCallSettings.getURL(state);
    }
}

@Component({
    selector: 'restCall-advanced-settings',
    templateUrl: './restcall.advanced.settings.html',
    styleUrls: ['./restCall.settings.css']
})
export class RestCallAdvancedSettings extends ARestCallSettings {

    static ModuleID = 'restAdvancedWFModule';

    public static getURL(status: WorkflowStatus) {
        let retVal = '';
        if (status) {
            const data = status.ActualSettings;
            if (data) {
                const calculator = new WorkflowFormulaCalculator(status);
                if (data.UseServiceURL) {
                    retVal = SettingsService.API_BASE_URL.getValue();
                } else {
                    if (data.Address) {
                        const address = calculator.CalcFormula(data.Address);
                        retVal += data.IsSSL ? 'https://' : 'http://';
                        retVal += address;
                        if (typeof data.Port === 'number' && data.Port > 0) {
                            retVal += ':' + data.Port;
                        }
                        retVal += '/';
                    } else {
                        retVal += SettingsService.API_BASE_URL.getValue();
                    }
                }
                if (data.Path) {
                    const path = calculator.CalcFormula(data.Path);
                    retVal += path;
                }
                if (data.Params && data.Params.length > 0) {
                    retVal += '?';
                    let first = true;
                    data.Params.forEach(v => {
                        if (!first) {
                            retVal += '&';
                        }
                        first = false;
                        const key = calculator.CalcFormula(v.Key);
                        const val = calculator.CalcFormula(v.Value);
                        retVal += data.UrlEncodeParams ? encodeURIComponent(key) + '=' + encodeURIComponent(val ?? '') : key + '=' + val;
                    });
                }
            }
        }
        return retVal;
    }

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = RestCallAdvancedSettings.ModuleID;
        reg.Caption = '@@Restaufruf';
        reg.GroupID = 'wfActions';
        reg.Index = 30;
        reg.SettingsControl = RestCallAdvancedSettings;
        reg.SettingsTypeHelper = new RestCallAdvancedSettingsDataHelper();
        reg.Executer = RestCallAdvancedModuleExecuter;
        return reg;
    }

    constructor(protected translate: TranslateService) {
        super(translate);
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, RestCallAdvancedSettingsData);
        } else {
            this.Data = newSettings(new RestCallAdvancedSettingsData());
        }
    }

    getPreview(): string {
        try {
            const state = new WorkflowStatus('', '');
            state.ActualSettings = this.Data;
            return RestCallAdvancedSettings.getURL(state);
        } catch {
            return 'ERROR';
        }
    }
}

// @dynamic
export abstract class ARestCallSettingsData {
    Type: MethodType = MethodType.Get;
    UseServiceURL = false;
    IsSSL = false;
    IgnoreSSL = false;
    Address = '';
    Port = 0;
    Path = '';
    GetPathFromStatus = false;
    @Type(() => RestParamData)
    Params: RestParamData[] = [];
    @Type(() => RestParamData)
    Headers: RestParamData[] = [];
    AuthDefined = false;
    Username = '';
    Password = '';
    BodyType: BodyType = BodyType.SelfDefined;
    BodyContent = '';
    BodyContentType = 'application/json';
    ParseResultAsJSON = true;
    TimeoutSeconds: number = null;
    // Since 2026-10-20. Off/empty in workflows saved before; a new module turns the encoding on
    // (newSettings below), so an old module keeps sending its parameters as they are.
    UrlEncodeParams = false;
    StatusCodeKey = '';
    ResponseHeadersKey = '';
    ErrorBodyAsResult = false;
    // Paging, since 2026-10-20; None in older workflows (one call as before).
    Paging: RestPaging = RestPaging.None;
    PagingItemsPath = '';
    PagingOffsetParam = '';
    PagingSizeParam = '';
    PagingSize = 100;
    PagingFirstPage = 1;
    PagingNextLinkPath = '';
    PagingMaxPages = 1000;
    // Since 2026-10-20: address and sign-in from a REST data source (server only), 202 waited out.
    DataSource: string = null;
    FollowAccepted = false;
}

/** RestPlugIn.TypeGuid (MiddleShared/NonDatabase/REST/RestPlugIn.cs). */
const REST_DATENQUELLE = '0ccbe866-f06d-44a5-aad2-bdb11c9cf327';
/** Lexbizz PlugIn.TypeGuid (databases/Lexbizz/PlugIn.cs) -- Haufe X360, REST module needs its OAuth2 sign-in. */
const HAUFE_DATENQUELLE = '7562acba-2d6f-4594-83ba-c4d1423b26c7';

export enum RestPaging {
    None,
    Offset,
    Page,
    NextLink,
}

/** Value at a path like "data.items", "value[0]" or "['@odata.nextLink']" (JSONPath subset of SelectToken). */
export function pfadWert(objekt: any, pfad: string): any {
    let wert = objekt;
    const teile = pfad.match(/\['([^']*)'\]|\["([^"]*)"\]|\[(\d+)\]|([^.[\]]+)/g) ?? [];
    for (const teil of teile) {
        if (wert == null) {
            return undefined;
        }
        const m = /^\['(.*)'\]$|^\["(.*)"\]$|^\[(\d+)\]$/.exec(teil);
        wert = m ? wert[m[1] ?? m[2] ?? Number(m[3])] : wert[teil];
    }
    return wert;
}

function newSettings<T extends ARestCallSettingsData>(data: T): T {
    data.UrlEncodeParams = true;
    return data;
}

export class RestCallSettingsData extends ARestCallSettingsData {
}

export class RestCallAdvancedSettingsData extends ARestCallSettingsData {
}

export abstract class ARestCallSettingsDataHelper extends WorkflowModuleSettingsHelper {    

    getAdditionalCaption(settings) {
        if (settings && settings.Type) {
            return MethodType[settings.Type];
        }
        return super.getAdditionalCaption(settings);
    }

    getExitPoints(settings): WorkflowExitInfo[] {
        const success = new WorkflowExitInfo();
        success.Label = '@@Success';
        const error = new WorkflowExitInfo();
        error.ID = 1;
        error.Label = '@@Error';
        return [success, error];
    }
}

export class RestCallSettingsDataHelper extends ARestCallSettingsDataHelper {
    getEmptySettingsInstance() {
        return newSettings(new RestCallSettingsData());
    }
}

export class RestCallAdvancedSettingsDataHelper extends ARestCallSettingsDataHelper {
    getEmptySettingsInstance() {
        return newSettings(new RestCallAdvancedSettingsData());
    }
}

export class RestParamData {
    Key = '';
    Value = '';
    ReadFromStatus: false;
}

// @dynamic
export abstract class ARestCallModuleExecuter extends WorkflowModuleExecuter {
    async execute(status: WorkflowStatus): Promise<number> {
        return new Promise<number>(resolve => {
            if (status.ActualSettings) {
                let headers = new HttpHeaders();
                if (!status.ActualSettings.UseServiceURL) {
                    headers = headers.append(InterceptorSkipHeader, '');
                    if (status.ActualSettings.AuthDefined) {
                        const auth = 'Basic ' + btoa(status.ActualSettings.Username + ':' + status.ActualSettings.Password);
                        headers = headers.append('Authorization', auth);
                    }
                    headers = this.fillHeaders(headers, status);
                }
                let b = null;
                const settings = status.ActualSettings;
                const mitRumpf = settings.Type === MethodType.Post || settings.Type === MethodType.Put || settings.Type === MethodType.Patch;
                if (mitRumpf) {
                    if (settings.BodyContentType) {
                        headers = headers.append('Content-Type', settings.BodyContentType);
                    } else if (settings.UseServiceURL) {
                        headers = headers.append('Content-Type', 'application/json');
                    }
                    switch (settings.BodyType) {
                        case BodyType.ActualParameter:
                            b = status.ActualParameter;
                            break;
                        case BodyType.SelfDefined:
                            b = settings.BodyContent;
                            break;
                        case BodyType.Status:
                            b = status.Context.get(settings.BodyContent);
                            break;
                    }
                }
                if (settings.DataSource) {
                    // Sign-in data of a data source never reaches the browser.
                    status.Logger.logError('Rest modul: a REST data source works in service workflows only.');
                    status.ActualParameter = null;
                    resolve(-1);
                    return;
                }
                const methode = ARestCallModuleExecuter.Methoden[settings.Type];
                if (!methode) {
                    status.ActualParameter = null;
                    resolve(0);
                    return;
                }
                const url = this.getURL(status);
                const http = InjectorHelper.InjectorInstance.get<HttpClient>(HttpClient);
                if (settings.Paging) {
                    this.seiten(http, methode, url, headers, mitRumpf ? b : undefined, status).then(resolve);
                    return;
                }
                // observe: 'response' only for the status code and headers; body, success and error
                // handling are as before when the new settings are empty.
                http.request<any>(methode, url, { headers: headers, body: mitRumpf ? b : undefined, observe: 'response' }).pipe(
                    catchError((e: HttpErrorResponse) => {
                        status.Logger.logWarning('Rest modul: Error while executing ' + methode + ' (' + e.message + ')');
                        this.ablegen(status, e.status, e.headers);
                        status.ActualParameter = settings.ErrorBodyAsResult && e.status > 0 ? e.error : null;
                        resolve(1);
                        return of(undefined);
                    })).subscribe(antwort => {
                        if (antwort === undefined) {
                            return;
                        }
                        this.ablegen(status, antwort.status, antwort.headers);
                        status.ActualParameter = antwort.body;
                        resolve(0);
                    });
            } else {
                status.Logger.logError('Rest modul: No settings found.');
                resolve(super.execute(status));
            }
        });
    }

    static readonly Methoden: { [type: number]: string } = {
        [MethodType.Get]: 'GET', [MethodType.Put]: 'PUT', [MethodType.Post]: 'POST',
        [MethodType.Delete]: 'DELETE', [MethodType.Options]: 'OPTIONS', [MethodType.Patch]: 'PATCH'
    };

    /** Page after page until an empty one; the items of all pages as one list (as on the server). */
    private async seiten(http: HttpClient, methode: string, url: string, headers: HttpHeaders, body: any, status: WorkflowStatus): Promise<number> {
        const s = status.ActualSettings;
        const alle: any[] = [];
        const maximum = Math.max(1, s.PagingMaxPages || 1000);
        const groesse = Math.max(1, s.PagingSize || 100);
        const name = (x: string) => s.UrlEncodeParams ? encodeURIComponent(x) : x;
        let naechste = url;
        let letzte: HttpResponse<any> = null;
        let seite = 0;
        for (; seite < maximum; seite++) {
            let adresse = naechste;
            if (s.Paging !== RestPaging.NextLink) {
                const teile: string[] = [];
                if (s.PagingOffsetParam) {
                    teile.push(name(s.PagingOffsetParam) + '=' + (s.Paging === RestPaging.Offset ? seite * groesse : (s.PagingFirstPage ?? 1) + seite));
                }
                if (s.PagingSizeParam) {
                    teile.push(name(s.PagingSizeParam) + '=' + groesse);
                }
                if (teile.length) {
                    adresse = url + (url.includes('?') ? '&' : '?') + teile.join('&');
                }
            }
            try {
                letzte = await firstValueFrom(http.request<any>(methode, adresse, { headers: headers, body: body, observe: 'response' }));
            } catch (e) {
                const fehler = e as HttpErrorResponse;
                status.Logger.logWarning('Rest modul: Error while executing ' + methode + ' (' + fehler?.message + ')');
                this.ablegen(status, fehler?.status, fehler?.headers);
                status.ActualParameter = s.ErrorBodyAsResult && fehler?.status > 0 ? fehler.error : null;
                return 1;
            }
            const antwort = letzte.body;
            const eintraege = s.PagingItemsPath ? pfadWert(antwort, s.PagingItemsPath) : antwort;
            if (eintraege != null && !Array.isArray(eintraege)) {
                status.Logger.logError("Paging: '" + (s.PagingItemsPath ?? '') + "' is not a list in the response.");
                status.ActualParameter = null;
                return -1;
            }
            if (!eintraege?.length) {
                break;
            }
            alle.push(...eintraege);
            if (s.Paging === RestPaging.NextLink) {
                const link = s.PagingNextLinkPath ? pfadWert(antwort, s.PagingNextLinkPath) : null;
                if (!link) {
                    break;
                }
                // Same origin only: headers, basic auth and (via the interceptor) the bearer token go
                // with every page; an answer must not be able to send them to another host.
                const bisher = new URL(naechste, location.href);
                const folge = new URL(String(link), bisher);
                if (folge.origin !== bisher.origin) {
                    status.Logger.logError('Paging: the next link points to another host and is not followed: ' + link);
                    status.ActualParameter = null;
                    return -1;
                }
                naechste = folge.toString();
            }
        }
        if (seite >= maximum) {
            status.Logger.logWarning('Paging stopped after ' + maximum + ' pages (PagingMaxPages); there may be more.');
        }
        this.ablegen(status, letzte?.status, letzte?.headers);
        status.ActualParameter = alle;
        return 0;
    }

    /** Status code and headers into the status variables, if the module names them. */
    private ablegen(status: WorkflowStatus, code: number, kopf: HttpHeaders) {
        const settings = status.ActualSettings;
        if (settings.StatusCodeKey) {
            status.Context.set(settings.StatusCodeKey, code ?? 0);
        }
        if (settings.ResponseHeadersKey) {
            const werte = {};
            kopf?.keys().forEach(k => werte[k] = kopf.getAll(k)?.join(', '));
            status.Context.set(settings.ResponseHeadersKey, werte);
        }
    }

    abstract fillHeaders(headers: HttpHeaders, status: WorkflowStatus): HttpHeaders;
    abstract getURL(status: WorkflowStatus): string;
}

export class RestCallModuleExecuter extends ARestCallModuleExecuter {
    fillHeaders(headers: HttpHeaders, status: WorkflowStatus): HttpHeaders {
        status.ActualSettings.Headers.forEach(h => {
            let val = '';
            if (h.ReadFromStatus) {
                const stateVal = status.Context.get(h.Value);
                if (stateVal) {
                    val = stateVal.toString();
                }
            } else {
                val = h.Value;
            }
            headers = headers.append(h.Key, val);
        });
        return headers;
    }
    getURL(status: WorkflowStatus): string {
        return RestCallSettings.getURL(status);
    }
}

export class RestCallAdvancedModuleExecuter extends ARestCallModuleExecuter {
    fillHeaders(headers: HttpHeaders, status: WorkflowStatus): HttpHeaders {
        const calculator = new WorkflowFormulaCalculator(status);
        status.ActualSettings.Headers.forEach(h => {
            const key = calculator.CalcFormula(h.Key);
            const val = calculator.CalcFormula(h.Value);
            headers = headers.append(key, val);
        });
        return headers;
    }
    getURL(status: WorkflowStatus): string {
        return RestCallAdvancedSettings.getURL(status);
    }
}
